import { Editor } from '@tinymce/tinymce-react';
import { useField } from 'formik';
import { useRef, useCallback } from 'react';

interface FormikTinyEditorProps {
    name: string;
    content?: string;
    readOnly?: boolean;
    disabled?: boolean;
    height?: number;
}

export function FormikTinyEditor({
    name,
    content = '',
    readOnly = false,
    disabled = false,
    height = 300,
}: FormikTinyEditorProps) {
    const [field, meta, helpers] = useField(name);
    const editorRef = useRef<any>(null);
    const initialValueRef = useRef(field.value || content || '');

    const handleEditorChange = useCallback((newContent: string) => {
        helpers.setValue(newContent);
    }, [helpers]);

    const editorConfig = {
        height: height,
        menubar: false,
        plugins: [
            'lists', 'link', 'image', 'code', 'directionality'
        ],
        toolbar: 'undo redo | bold italic | alignleft aligncenter alignright | ltr rtl | bullist numlist | link image | code',
        // RTL support for Persian/Arabic languages
        directionality: 'rtl' as const,
        // Content styling with RTL support
        content_style: `
            body { 
                font-family: 'Tahoma', 'Arial', sans-serif; 
                font-size: 14px; 
                direction: rtl; 
                text-align: right;
            }
        `,
        // Important: prevent content initialization issues
        entity_encoding: 'raw' as const,
        // Fix backspace and keyboard issues
        forced_root_block: 'p' as const,
        remove_trailing_brs: false,
        // Link plugin settings
        link_default_target: '_blank',
        link_assume_external_targets: true,
        link_context_toolbar: true,
        // Disable promotions to fix dialog issues
        promotion: false,
        // Fix z-index for dialogs inside Bootstrap modals
        ui_mode: 'split' as const,
        // Ensure proper initialization
        init_instance_callback: (editor: any) => {
            editorRef.current = editor;
        }
    };

    return (
        <div className="formik-tiny-editor" dir="rtl">
            <Editor
                apiKey="nv3u5u3e2lqi8r4u1zxgjsjxa9oiyt1kre9i8e5016urruxk"
                licenseKey="gpl"
                onInit={(evt, editor) => {
                    editorRef.current = editor;
                }}
                initialValue={initialValueRef.current}
                onEditorChange={handleEditorChange}
                init={editorConfig}
                disabled={disabled}
            />
            {meta.touched && meta.error && (
                <div className="invalid-feedback d-block">
                    {meta.error}
                </div>
            )}
        </div>
    );
}
